import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { toast } from "sonner";
import { MapPin, Check, X, RotateCcw, LocateFixed } from "lucide-react";

const STATUTS = { BROUILLON: "Brouillon", EN_ATTENTE: "En attente", VALIDE: "Validé", REFUSE: "Refusé" };
const BADGE = {
  BROUILLON: "bg-slate-100 text-slate-600",
  EN_ATTENTE: "bg-cfhtb-accent/15 text-cfhtb-accent",
  VALIDE: "bg-cfhtb-success/10 text-cfhtb-success",
  REFUSE: "bg-cfhtb-error/10 text-cfhtb-error",
};

export default function AnnuaireAdmin() {
  const [data, setData] = useState(null);
  const [statut, setStatut] = useState("EN_ATTENTE");
  const [geocoding, setGeocoding] = useState(false);

  const load = () =>
    api(`/admin/annuaire${statut ? `?statut=${statut}` : ""}`).then(setData).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, [statut]);

  const setValidation = async (fiche, s) => {
    try {
      await api(`/admin/annuaire/${fiche.id}/validation`, { method: "POST", body: { statut: s } });
      toast.success(`Fiche ${STATUTS[s].toLowerCase()}`);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const geocoder = async () => {
    setGeocoding(true);
    try {
      const r = await api("/admin/annuaire/geocoder", { method: "POST" });
      toast.success(`Géocodage terminé : ${r.geocodees} réussie(s), ${r.echecs} échec(s)`);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setGeocoding(false);
    }
  };

  return (
    <AppLayout variant="admin">
      <div data-testid="annuaire-admin-page" className="space-y-5 animate-fade-in">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-serif text-3xl font-bold text-cfhtb-heading tracking-tight">Annuaire des praticiens</h1>
            <p className="text-sm text-cfhtb-text mt-1">Validation des fiches publiées sur la carte de l'espace adhérent.</p>
          </div>
          <button
            data-testid="geocoder-button"
            onClick={geocoder}
            disabled={geocoding}
            className="inline-flex items-center gap-2 bg-cfhtb-secondary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-cfhtb-primary transition-colors disabled:opacity-50"
          >
            <LocateFixed className="h-4 w-4" />
            {geocoding ? "Géocodage en cours (~1 s/fiche)…" : "Géocoder les fiches sans coordonnées"}
          </button>
        </div>

        <div className="flex flex-wrap gap-2" data-testid="annuaire-statuts">
          {Object.entries(STATUTS).map(([k, label]) => (
            <button
              key={k}
              data-testid={`filtre-statut-${k.toLowerCase()}`}
              onClick={() => setStatut(k)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                statut === k ? "bg-cfhtb-primary text-white" : "bg-white border border-slate-200 text-cfhtb-text hover:border-cfhtb-light"
              }`}
            >
              {label} ({data?.compteurs?.[k] ?? "…"})
            </button>
          ))}
          <button
            data-testid="filtre-statut-tous"
            onClick={() => setStatut("")}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              statut === "" ? "bg-cfhtb-primary text-white" : "bg-white border border-slate-200 text-cfhtb-text hover:border-cfhtb-light"
            }`}
          >
            Toutes
          </button>
        </div>

        {!data ? (
          <div className="h-8 w-8 border-2 border-cfhtb-primary border-t-transparent rounded-full animate-spin" />
        ) : data.fiches.length === 0 ? (
          <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6 text-sm text-cfhtb-text" data-testid="annuaire-admin-vide">
            Aucune fiche dans ce statut.
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm overflow-hidden" data-testid="annuaire-admin-liste">
            <ul className="divide-y divide-slate-100">
              {data.fiches.map((f) => (
                <li key={f.id} className="px-5 py-4 flex flex-wrap items-center gap-4" data-testid={`fiche-row-${f.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-cfhtb-heading">
                      {f.nom_complet || "—"} {f.cabinet_nom ? <span className="text-cfhtb-text font-normal">· {f.cabinet_nom}</span> : null}
                    </p>
                    <p className="text-xs text-cfhtb-text mt-0.5 inline-flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {[f.adresse, f.code_postal, f.ville, f.pays].filter(Boolean).join(", ") || "Adresse non renseignée"}
                      {f.latitude != null
                        ? <span className="text-cfhtb-success ml-1">· géocodée</span>
                        : <span className="text-cfhtb-accent ml-1">· non géocodée</span>}
                    </p>
                    {f.specialites?.length > 0 && (
                      <p className="text-xs text-cfhtb-secondary mt-1">{f.specialites.join(" · ")}</p>
                    )}
                  </div>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${BADGE[f.statut_validation] || BADGE.BROUILLON}`}
                    data-testid={`fiche-statut-${f.id}`}>
                    {STATUTS[f.statut_validation] || f.statut_validation}
                  </span>
                  {!f.visible_sur_carte && (
                    <span className="text-xs text-cfhtb-text">Non visible sur la carte</span>
                  )}
                  <div className="flex items-center gap-2">
                    {f.statut_validation !== "VALIDE" && (
                      <button data-testid={`valider-${f.id}`} onClick={() => setValidation(f, "VALIDE")}
                        className="inline-flex items-center gap-1 text-xs font-medium text-cfhtb-success border border-cfhtb-success/40 rounded-md px-2.5 py-1.5 hover:bg-cfhtb-success/5 transition-colors">
                        <Check className="h-3.5 w-3.5" /> Valider
                      </button>
                    )}
                    {f.statut_validation !== "REFUSE" && (
                      <button data-testid={`refuser-${f.id}`} onClick={() => setValidation(f, "REFUSE")}
                        className="inline-flex items-center gap-1 text-xs font-medium text-cfhtb-error border border-cfhtb-error/40 rounded-md px-2.5 py-1.5 hover:bg-cfhtb-error/5 transition-colors">
                        <X className="h-3.5 w-3.5" /> Refuser
                      </button>
                    )}
                    {f.statut_validation !== "EN_ATTENTE" && (
                      <button data-testid={`remettre-${f.id}`} onClick={() => setValidation(f, "EN_ATTENTE")}
                        className="inline-flex items-center gap-1 text-xs font-medium text-cfhtb-text border border-slate-200 rounded-md px-2.5 py-1.5 hover:bg-cfhtb-bg transition-colors">
                        <RotateCcw className="h-3.5 w-3.5" /> Remettre en attente
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
